import React, { useState, useRef, useEffect } from 'react';
import { 
    Upload, Download, Copy, Trash2, 
    CheckCircle, Image as ImageIcon, MessageSquare,
    Share2, Zap, Edit3, Calendar,
    Lightbulb, WandSparkles, Layout, AlertCircle, BrainCircuit,
    Instagram, Twitter, ExternalLink, Loader2, ScanEye, Plus, Target, Rocket
} from 'lucide-react';
import html2canvas from 'html2canvas';

// Shared UI
import { 
    AppButton, BlackboxLoaderIcon, SectionTitle, AppInput, AppTextarea
} from '../../components/SharedUI';

// Firebase
import { collection, addDoc } from 'firebase/firestore'; 
import { ref, getDownloadURL, uploadBytes } from 'firebase/storage';

// Services
import { analyzeSocialScreenshotLLM, generateRemixContentLLM, callImagenApi, callGeminiApi } from '../../services/aiService';

// --- PREMIUM OVERLAY ---
const PremiumSocialOverlay = ({ isVisible, mode, currentStepIndex }) => {
    if (!isVisible) return null;
    const analysisSteps = ["Ingesting Social Screenshots...", "Scanning for Text Layers (OCR)...", "Analyzing Sentiment & Context...", "Extracting User Feedback Data...", "Finalizing Social Proof Dataset..."];
    const generationSteps = ["Analyzing Selected Reviews...", "Drafting High-Engagement Copy...", "Designing Visual Background Assets...", "Compositing Remix Graphic...", "Finalizing Campaign Assets..."];
    const steps = mode === 'analysis' ? analysisSteps : generationSteps;
    const progress = Math.min(((currentStepIndex + 1) / steps.length) * 100, 100);
    const bgClass = mode === 'analysis' ? 'bg-blue-500' : 'bg-purple-500';
    const gradientClass = mode === 'analysis' ? 'from-blue-600 to-cyan-400' : 'from-purple-600 to-pink-500';

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/90 backdrop-blur-xl animate-in fade-in duration-300">
            <div className="w-full max-w-lg p-8 relative">
                <div className="flex justify-center mb-10">
                    <div className="relative">
                        <div className={`absolute inset-0 ${bgClass} blur-2xl opacity-20 animate-pulse rounded-full`}></div>
                        <BlackboxLoaderIcon className="w-20 h-20 relative z-10 text-white" variant="white-pulse" />
                    </div>
                </div>
                <div className="relative w-full h-1 bg-zinc-800/50 rounded-full mb-8 overflow-hidden">
                    <div className={`h-full bg-gradient-to-r ${gradientClass} transition-all duration-500 ease-out`} style={{ width: `${progress}%` }}></div>
                </div>
                <div className="space-y-3 font-mono text-xs">
                    {steps.map((step, index) => (
                        <div key={index} className={`flex items-center gap-3 transition-all duration-300 ${index === currentStepIndex ? 'translate-x-2 text-white font-bold' : index < currentStepIndex ? 'text-purple-300/50' : 'text-zinc-700'}`}>
                            <div className={`w-4 h-4 rounded-full flex items-center justify-center border ${index < currentStepIndex ? 'border-purple-500 bg-purple-500/20' : index === currentStepIndex ? 'border-white bg-white text-black' : 'border-zinc-800 bg-zinc-900'}`}>
                                {index < currentStepIndex && <CheckCircle size={10} />}
                                {index === currentStepIndex && <Loader2 size={10} className="animate-spin" />}
                            </div>
                            <span>{step}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// --- UTILS ---
const urlToBase64 = async (url) => {
    try {
        const response = await fetch(url);
        const blob = await response.blob();
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
        });
    } catch { return null; }
};

// --- UPGRADED ALGORITHMIC SCHEDULE MODAL ---
const ScheduleRemixModal = ({ isOpen, onClose, captions, backgroundUrl, filmData, db, user, setNotification }) => {
    const [strategy, setStrategy] = useState(null);
    const [loadingStrategy, setLoadingStrategy] = useState(false);
    const [scheduleDate, setScheduleDate] = useState('');
    const [selectedPlatform, setSelectedPlatform] = useState('instagram'); 

    if (!isOpen) return null;

    const handleGenerateStrategy = async () => {
        setLoadingStrategy(true);
        try {
            const currentCaption = selectedPlatform === 'instagram' ? captions.instagram : captions.twitter;
            const prompt = `
            You are an elite Meta/TikTok media buyer. We are launching a "Social Proof" (Review/Testimonial) ad for the film "${filmData.title}".
            Platform: ${selectedPlatform}
            Caption: "${currentCaption}"
            
            CRITICAL CONTEXT: Because this is a review/testimonial, it performs best as a MID-FUNNEL RETARGETING asset, not a cold-audience hook.
            
            Return a JSON object with these exact keys:
            1. "targeting_directive": Instructions on how to build a retargeting audience (e.g., "Retarget users who watched 50%+ of the broad top-funnel trailers").
            2. "budget_velocity": How to pace the retargeting spend (e.g., smaller daily budget to avoid rapid ad fatigue).
            3. "fatigue_signal": The metric indicating the retargeting pool is exhausted (e.g., "Ad Frequency exceeds 3.5").
            `;
            
            const schema = {
                type: "OBJECT",
                properties: {
                    targeting_directive: { type: "STRING" },
                    budget_velocity: { type: "STRING" },
                    fatigue_signal: { type: "STRING" }
                },
                required: ["targeting_directive", "budget_velocity", "fatigue_signal"]
            };

            const result = await callGeminiApi(prompt, {
                responseMimeType: "application/json",
                responseSchema: schema
            });
            
            setStrategy(result);
        } catch (error) { 
            console.error("Strategy Gen Error:", error);
            setNotification({ type: 'error', message: 'Strategy failed. Check console.' }); 
        } 
        finally { setLoadingStrategy(false); }
    };

    const saveToCampaigns = async (status = 'Draft') => {
        try {
            const content = selectedPlatform === 'instagram' ? captions.instagram : captions.twitter;
            
            const newCampaign = {
                title: `Social Proof: ${selectedPlatform.toUpperCase()} Quote`,
                content: content,
                platform: selectedPlatform,
                status: status, // 'Draft' for Laboratory, 'Scheduled' for Incubator
                scheduledDate: scheduleDate || null,
                creative: { url: backgroundUrl },
                conceptName: 'Social Proof Remix',
                hypothesis: 'Retargeting warm audiences with high-sentiment reviews to drive conversion.',
                film_id: filmData.id,
                user_id: user.uid,
                type: 'social_proof',
                created_at: new Date()
            };

            await addDoc(collection(db, 'campaigns'), newCampaign);
            return true;
        } catch (error) {
            console.error("Save Error:", error);
            return false;
        }
    };

    const handlePushToIncubator = async () => {
        if (!scheduleDate) return setNotification({ type: 'error', message: 'Please select a test initiation date.' });
        
        const success = await saveToCampaigns('Scheduled');
        if (success) {
            setNotification({ type: 'success', message: 'Asset pushed to The Incubator!' });
            onClose();
        } else {
            setNotification({ type: 'error', message: 'Failed to push asset.' });
        }
    };

    const handlePushToLaboratory = async () => {
        const success = await saveToCampaigns('Draft');
        if (success) {
            setNotification({ type: 'success', message: 'Asset saved to The Laboratory.' });
            onClose();
        } else {
            setNotification({ type: 'error', message: 'Failed to save asset.' });
        }
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4" onClick={onClose}>
            <div className="bg-zinc-950 rounded-2xl p-6 max-w-2xl w-full border border-zinc-800 shadow-[0_0_50px_rgba(0,0,0,0.8)]" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-6 pb-4 border-b border-zinc-800">
                    <div>
                        <h2 className="text-xl font-bold text-white flex items-center gap-2 font-poppins">
                            <Target className="text-blue-500" /> Retargeting Protocol
                        </h2>
                        <p className="text-[10px] text-zinc-500 uppercase tracking-widest mt-1">Mid-Funnel Asset Deployment</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-full text-zinc-500 hover:text-white transition-colors">
                        <Plus size={20} className="rotate-45" />
                    </button>
                </div>
                
                <div className="space-y-6">
                    <div className="flex gap-2">
                        {['instagram', 'twitter'].map(p => (
                            <button key={p} onClick={() => setSelectedPlatform(p)} className={`flex-1 py-3 rounded-lg font-bold text-xs uppercase tracking-wide transition-all ${selectedPlatform === p ? 'bg-blue-600/20 text-blue-400 border border-blue-500/50' : 'bg-zinc-900 text-zinc-500 border border-zinc-800'}`}>
                                {p} Format
                            </button>
                        ))}
                    </div>
                    
                    {!strategy ? (
                        <AppButton onClick={handleGenerateStrategy} disabled={loadingStrategy} variant="secondary" icon={BrainCircuit} className="w-full bg-zinc-900 border-zinc-800 py-6">
                            {loadingStrategy ? 'Analyzing Retargeting Parameters...' : 'Calculate Retargeting Strategy'}
                        </AppButton>
                    ) : (
                        <div className="bg-gradient-to-r from-blue-900/10 to-purple-900/10 border border-blue-500/20 p-5 rounded-xl space-y-4 animate-in fade-in">
                            <div className="flex gap-3 items-start">
                                <Target size={14} className="text-blue-400 mt-1 shrink-0"/>
                                <div>
                                    <p className="text-[10px] text-blue-300 font-bold uppercase mb-1">Audience Directive</p>
                                    <p className="text-white text-xs leading-relaxed">{strategy.targeting_directive}</p>
                                </div>
                            </div>
                            <div className="flex gap-3 items-start">
                                <Zap size={14} className="text-amber-400 mt-1 shrink-0"/>
                                <div>
                                    <p className="text-[10px] text-amber-300 font-bold uppercase mb-1">Budget Velocity</p>
                                    <p className="text-zinc-300 text-xs leading-relaxed">{strategy.budget_velocity}</p>
                                </div>
                            </div>
                            <div className="flex gap-3 items-start">
                                <AlertCircle size={14} className="text-red-400 mt-1 shrink-0"/>
                                <div>
                                    <p className="text-[10px] text-red-300 font-bold uppercase mb-1">Fatigue Signal</p>
                                    <p className="text-zinc-300 text-xs leading-relaxed">{strategy.fatigue_signal}</p>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    <div className="pt-4 border-t border-zinc-800 space-y-4">
                        <div>
                            <label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-2">Test Initiation Date</label>
                            <input 
                                type="date" 
                                value={scheduleDate} 
                                onChange={(e) => setScheduleDate(e.target.value)} 
                                className="w-full px-4 py-3 bg-zinc-900 text-white text-sm rounded-lg border border-zinc-800 focus:border-blue-500 outline-none transition-colors" 
                            />
                        </div>
                        
                        <div className="flex gap-3 pt-2">
                            <AppButton onClick={handlePushToLaboratory} variant="secondary" icon={Layout} className="flex-1 bg-zinc-900 border-zinc-700 hover:bg-zinc-800 justify-center">
                                Save to Laboratory
                            </AppButton>
                            <AppButton onClick={handlePushToIncubator} disabled={!scheduleDate || !strategy} variant="primary" icon={Rocket} className="flex-1 bg-blue-600 hover:bg-blue-500 justify-center">
                                Push to Incubator
                            </AppButton>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// --- MAIN STUDIO COMPONENT ---
const SocialProofStudio = ({ filmData, setNotification, saveFilmData, storage, credits, deductCredit, db, user }) => {
    const [activeTab, setActiveTab] = useState('library'); 
    const [uploadedImages, setUploadedImages] = useState([]);
    const [extractedFeedback, setExtractedFeedback] = useState([]);
    const [selectedFeedbackId, setSelectedFeedbackId] = useState(null);
    
    const [remixContent, setRemixContent] = useState(null);
    const [editableCaptions, setEditableCaptions] = useState({ instagram: '', twitter: '' });
    
    const [generatedBackground, setGeneratedBackground] = useState(null);
    const [bgSource, setBgSource] = useState('ai'); 
    const [visualStyle, setVisualStyle] = useState('modern');
    
    const canvasRef = useRef(null);
    
    const [loading, setLoading] = useState(false);
    const [loadingMode, setLoadingMode] = useState('analysis');
    const [loadingStepIndex, setLoadingStepIndex] = useState(0);
    const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
    const [manualEntry, setManualEntry] = useState({ name: '', platform: 'twitter', text: '' });
    
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (filmData?.social_proof_images) setUploadedImages(filmData.social_proof_images);
        if (filmData?.social_proof_feedback) setExtractedFeedback(filmData.social_proof_feedback);
        if (filmData?.social_proof_remix) {
            setRemixContent(filmData.social_proof_remix);
            setEditableCaptions({
                instagram: filmData.social_proof_remix.caption_instagram || '',
                twitter: filmData.social_proof_remix.caption_twitter || ''
            });
            setGeneratedBackground(filmData.social_proof_bg);
        }
    }, [filmData]);

    const activeFeedback = extractedFeedback.find(f => f.id === selectedFeedbackId);
    const posterUrl = filmData.poster_a || filmData.posterUrl;
    const currentBackground = bgSource === 'poster' && posterUrl ? posterUrl : generatedBackground;

    const handleFileUpload = async (files) => {
        const newImages = [];
        for (const file of files) {
            const reader = new FileReader();
            reader.onload = (e) => {
                newImages.push({ file, url: e.target.result, id: Date.now() + Math.random() });
                if (newImages.length === files.length) setUploadedImages(prev => [...prev, ...newImages]);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleManualEntry = async () => {
        if (!manualEntry.name || !manualEntry.text) return setNotification({ type: 'error', message: 'Required fields missing' });
        const newFeedback = {
            id: `fb_manual_${Date.now()}`,
            commenterName: manualEntry.name,
            platform: manualEntry.platform,
            commentText: manualEntry.text,
            sentiment: 'Positive',
            selected: true
        };
        const updatedList = [newFeedback, ...extractedFeedback];
        setExtractedFeedback(updatedList);
        setSelectedFeedbackId(newFeedback.id); 
        setManualEntry({ name: '', platform: 'twitter', text: '' });
        await saveFilmData({ ...filmData, social_proof_feedback: updatedList });
        setNotification({ type: 'success', message: 'Quote added to Library' });
    };

    const handleAnalyze = async () => {
        if (uploadedImages.length === 0) return;
        if (credits < uploadedImages.length) return setNotification({ type: 'error', message: 'Insufficient credits' });

        setLoading(true); setLoadingMode('analysis');
        try {
            const interval = setInterval(() => setLoadingStepIndex(prev => prev < 4 ? prev + 1 : prev), 1000);
            
            const newFeedback = [];
            for (let i = 0; i < uploadedImages.length; i++) {
                let b64 = uploadedImages[i].url;
                if (b64.startsWith('http')) b64 = await urlToBase64(b64);
                if (b64) {
                    const res = await analyzeSocialScreenshotLLM(b64);
                    if (res) newFeedback.push({ id: `fb_${Date.now()}_${i}`, imageId: uploadedImages[i].id, ...res });
                }
            }
            clearInterval(interval);
            
            const updated = [...newFeedback, ...extractedFeedback];
            setExtractedFeedback(updated);
            await saveFilmData({ ...filmData, social_proof_feedback: updated });
            await deductCredit(uploadedImages.length);
            setNotification({ type: 'success', message: `Extracted ${newFeedback.length} quotes` });
            setUploadedImages([]); 
        } catch (e) { console.error(e); setNotification({ type: 'error', message: 'Analysis failed' }); } 
        finally { setLoading(false); setLoadingStepIndex(0); }
    };

    const handleGenerateRemix = async () => {
        if (!activeFeedback) return;
        if (credits < 2) return setNotification({ type: 'error', message: 'Insufficient credits' });

        setLoading(true); setLoadingMode('generation');
        try {
            const interval = setInterval(() => setLoadingStepIndex(prev => prev < 4 ? prev + 1 : prev), 1200);
            
            const strategy = await generateRemixContentLLM(filmData, [activeFeedback]);
            setRemixContent(strategy);
            setEditableCaptions({
                instagram: strategy.caption_instagram,
                twitter: strategy.caption_twitter
            });

            if (bgSource === 'ai') {
                const b64 = await callImagenApi(strategy.visual_prompt);
                if (b64 && b64.startsWith('data:')) {
                    const url = await uploadBytes(ref(storage, `${filmData.id}/social_proof/bg_${Date.now()}.png`), await (await fetch(b64)).blob()).then(r => getDownloadURL(r.ref));
                    setGeneratedBackground(url);
                    await saveFilmData({ ...filmData, social_proof_remix: strategy, social_proof_bg: url });
                }
            } else {
                 await saveFilmData({ ...filmData, social_proof_remix: strategy });
            }

            clearInterval(interval);
            await deductCredit(2);
            setNotification({ type: 'success', message: 'Remix Generated!' });
        } catch (e) { console.error(e); setNotification({ type: 'error', message: 'Generation failed' }); }
        finally { setLoading(false); setLoadingStepIndex(0); }
    };

    const handleDownloadAsset = async () => {
        if (!canvasRef.current) return;
        try {
            setNotification({ type: 'info', message: 'Processing download...' });
            const canvas = await html2canvas(canvasRef.current, {
                useCORS: true, 
                scale: 2, 
                backgroundColor: null 
            });
            const image = canvas.toDataURL("image/png");
            const link = document.createElement('a');
            link.href = image;
            link.download = `social_proof_${filmData.title.replace(/\s+/g, '_')}_${Date.now()}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setNotification({ type: 'success', message: 'Asset downloaded!' });
        } catch (error) {
            console.error("Download failed:", error);
            setNotification({ type: 'error', message: 'Download failed. Check CORS or try again.' });
        }
    };

    const getStyleClasses = () => {
        switch (visualStyle) {
            case 'cinematic':
                return "bg-black/80 backdrop-blur-sm border-y border-purple-500/50 p-8 text-center font-serif tracking-widest";
            case 'bold':
                return "bg-purple-600/90 rotate-1 p-8 text-left font-sans font-black uppercase tracking-tighter shadow-[10px_10px_0px_rgba(0,0,0,1)]";
            default:
                return "bg-zinc-900/90 backdrop-blur-md rounded-2xl p-8 text-left font-poppins shadow-2xl border border-white/10";
        }
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 h-full">
            <PremiumSocialOverlay isVisible={loading} mode={loadingMode} currentStepIndex={loadingStepIndex} />
            
            <ScheduleRemixModal 
                isOpen={isScheduleModalOpen} 
                onClose={() => setIsScheduleModalOpen(false)} 
                captions={editableCaptions} 
                backgroundUrl={currentBackground} 
                filmData={filmData} 
                db={db} 
                user={user} 
                setNotification={setNotification} 
            />

            {/* LEFT COLUMN: SOURCE INTELLIGENCE */}
            <div className="lg:col-span-4 flex flex-col h-full gap-4">
                <div className="bg-black/40 backdrop-blur-md border border-white/10 rounded-2xl p-6 shadow-xl">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xs font-bold text-zinc-500 uppercase tracking-widest">Input Stream</h3>
                        <div className="flex gap-2">
                            <button onClick={() => setActiveTab('manual')} className={`p-2 rounded-lg transition-colors ${activeTab === 'manual' ? 'bg-purple-500 text-white' : 'bg-zinc-800 text-zinc-400'}`} title="Manual Entry"><Edit3 size={16}/></button>
                            <button onClick={() => setActiveTab('upload')} className={`p-2 rounded-lg transition-colors ${activeTab === 'upload' ? 'bg-purple-500 text-white' : 'bg-zinc-800 text-zinc-400'}`} title="Upload Screenshots"><Upload size={16}/></button>
                        </div>
                    </div>

                    {activeTab === 'manual' ? (
                        <div className="space-y-3 animate-fade-in">
                            <AppInput placeholder="@handle" value={manualEntry.name} onChange={e => setManualEntry({...manualEntry, name: e.target.value})} className="text-sm" />
                            <select value={manualEntry.platform} onChange={e => setManualEntry({...manualEntry, platform: e.target.value})} className="w-full bg-zinc-900 border border-zinc-700 rounded-lg p-3 text-sm text-zinc-300 outline-none">
                                <option value="twitter">Twitter / X</option><option value="instagram">Instagram</option><option value="letterboxd">Letterboxd</option>
                            </select>
                            <AppTextarea placeholder="Paste quote..." value={manualEntry.text} onChange={e => setManualEntry({...manualEntry, text: e.target.value})} rows={3} className="text-sm" />
                            <AppButton onClick={handleManualEntry} size="sm" icon={Plus} className="w-full">Add to Library</AppButton>
                        </div>
                    ) : (
                        <div className="space-y-4 animate-fade-in">
                            <div onClick={() => fileInputRef.current?.click()} className="border-2 border-dashed border-zinc-700 rounded-xl p-6 text-center hover:border-purple-500 cursor-pointer transition-colors bg-zinc-900/30">
                                <Upload className="mx-auto text-zinc-500 mb-2" size={20} />
                                <p className="text-xs text-zinc-400">Click to upload screenshots</p>
                            </div>
                            <input ref={fileInputRef} type="file" multiple accept="image/*" className="hidden" onChange={e => handleFileUpload(Array.from(e.target.files))} />
                            {uploadedImages.length > 0 && (
                                <AppButton onClick={handleAnalyze} variant="primary" size="sm" className="w-full" icon={Zap}>
                                    Analyze ({uploadedImages.length} Credits)
                                </AppButton>
                            )}
                        </div>
                    )}
                </div>

                <div className="flex-1 bg-zinc-900/30 border border-zinc-800 rounded-xl p-4 overflow-hidden flex flex-col">
                    <h4 className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-3">Quote Library ({extractedFeedback.length})</h4>
                    <div className="flex-1 overflow-y-auto space-y-2 custom-scrollbar pr-1">
                        {extractedFeedback.length === 0 ? (
                            <div className="text-center py-10 text-zinc-600 italic text-xs">No quotes yet. Add some above.</div>
                        ) : (
                            extractedFeedback.map(fb => (
                                <div 
                                    key={fb.id}
                                    onClick={() => setSelectedFeedbackId(fb.id)}
                                    className={`p-3 rounded-lg border cursor-pointer transition-all ${selectedFeedbackId === fb.id ? 'bg-purple-500/10 border-purple-500/50' : 'bg-black/20 border-white/5 hover:bg-black/40'}`}
                                >
                                    <div className="flex justify-between items-start mb-1">
                                        <span className={`text-xs font-bold ${selectedFeedbackId === fb.id ? 'text-white' : 'text-zinc-400'}`}>{fb.commenterName}</span>
                                        <span className="text-[10px] uppercase text-zinc-600">{fb.platform}</span>
                                    </div>
                                    <p className="text-xs text-zinc-400 line-clamp-2">"{fb.commentText}"</p>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>

            {/* RIGHT COLUMN: CREATIVE STUDIO */}
            <div className="lg:col-span-8 bg-black/40 backdrop-blur-xl border border-white/10 rounded-3xl p-6 shadow-2xl flex flex-col h-full relative overflow-hidden">
                <SectionTitle icon={WandSparkles}>Social Proof Studio</SectionTitle>
                
                <div className="flex-1 flex flex-col md:flex-row gap-6 min-h-0">
                    <div 
                        ref={canvasRef} 
                        className="flex-1 bg-zinc-950 rounded-2xl border border-zinc-800 relative overflow-hidden group flex items-center justify-center"
                    >
                        {currentBackground && (
                            <div className="absolute top-4 right-4 z-50 opacity-0 group-hover:opacity-100 transition-opacity" data-html2canvas-ignore="true">
                                <AppButton onClick={handleDownloadAsset} size="sm" variant="secondary" icon={Download} className="bg-black/60 backdrop-blur-md border-zinc-700 hover:bg-black/80 shadow-xl">
                                    Download Graphic
                                </AppButton>
                            </div>
                        )}

                        {!activeFeedback ? (
                            <div className="text-center text-zinc-600">
                                <Layout size={48} className="mx-auto mb-4 opacity-50" />
                                <p className="text-sm">Select a quote from the library to begin</p>
                            </div>
                        ) : (
                            <>
                                {currentBackground ? (
                                    <img src={currentBackground} crossOrigin="anonymous" className="absolute inset-0 w-full h-full object-cover opacity-60" alt="bg" />
                                ) : (
                                    <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-purple-900 to-black opacity-80" />
                                )}

                                <div className={`relative z-10 max-w-[80%] transition-all duration-300 ${getStyleClasses()}`}>
                                    <p className="text-white text-lg md:text-xl leading-relaxed mb-4">
                                        "{activeFeedback.commentText}"
                                    </p>
                                    <div className="flex items-center gap-2">
                                        <div className="w-6 h-0.5 bg-yellow-500"></div>
                                        <p className="text-zinc-300 font-bold uppercase text-xs tracking-widest">
                                            {activeFeedback.commenterName}
                                        </p>
                                    </div>
                                    {visualStyle === 'bold' && <div className="absolute -top-4 -right-4 text-4xl">❝</div>}
                                </div>
                            </>
                        )}
                    </div>

                    <div className="w-full md:w-80 flex flex-col gap-4 overflow-y-auto">
                        <div className="bg-zinc-900/50 p-4 rounded-xl border border-white/5">
                            <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-3 block">Visual Style</label>
                            <div className="grid grid-cols-3 gap-2 mb-4">
                                {['modern', 'cinematic', 'bold'].map(s => (
                                    <button 
                                        key={s} 
                                        onClick={() => setVisualStyle(s)}
                                        className={`py-2 text-[10px] font-bold uppercase rounded border transition-all ${visualStyle === s ? 'bg-purple-500 text-white border-purple-400' : 'bg-black text-zinc-500 border-zinc-800'}`}
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>

                            <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-3 block">Background Source</label>
                            <div className="flex gap-2">
                                <button onClick={() => setBgSource('ai')} className={`flex-1 py-2 text-[10px] font-bold uppercase rounded border ${bgSource === 'ai' ? 'bg-blue-500 text-white border-blue-400' : 'bg-black text-zinc-500 border-zinc-800'}`}>AI Generated</button>
                                <button onClick={() => setBgSource('poster')} disabled={!posterUrl} className={`flex-1 py-2 text-[10px] font-bold uppercase rounded border ${bgSource === 'poster' ? 'bg-green-500 text-white border-green-400' : 'bg-black text-zinc-500 border-zinc-800 disabled:opacity-50'}`}>Poster</button>
                            </div>
                        </div>

                        <div className="bg-zinc-900/50 p-4 rounded-xl border border-white/5 flex-1 flex flex-col">
                            {!remixContent ? (
                                <div className="text-center my-auto">
                                    <p className="text-xs text-zinc-400 mb-4">Ready to generate retargeting assets?</p>
                                    <AppButton onClick={handleGenerateRemix} disabled={!activeFeedback} variant="primary" icon={WandSparkles} className="w-full shadow-lg shadow-purple-900/20">
                                        Remix Asset (2 Credits)
                                    </AppButton>
                                </div>
                            ) : (
                                <div className="space-y-4 animate-fade-in">
                                    <div>
                                        <div className="flex justify-between mb-1">
                                            <span className="text-[10px] font-bold text-zinc-500 uppercase">Instagram Caption</span> 
                                            <Copy size={12} className="text-zinc-500 cursor-pointer" onClick={() => navigator.clipboard.writeText(editableCaptions.instagram)}/>
                                        </div>
                                        <textarea 
                                            value={editableCaptions.instagram}
                                            onChange={(e) => setEditableCaptions({...editableCaptions, instagram: e.target.value})}
                                            className="w-full bg-black/30 p-2 rounded text-xs text-zinc-300 h-20 border border-transparent focus:border-purple-500 outline-none resize-none"
                                        />
                                    </div>
                                    
                                    <div>
                                        <div className="flex justify-between mb-1">
                                            <span className="text-[10px] font-bold text-zinc-500 uppercase">Twitter / X Caption</span> 
                                            <Copy size={12} className="text-zinc-500 cursor-pointer" onClick={() => navigator.clipboard.writeText(editableCaptions.twitter)}/>
                                        </div>
                                        <textarea 
                                            value={editableCaptions.twitter}
                                            onChange={(e) => setEditableCaptions({...editableCaptions, twitter: e.target.value})}
                                            className="w-full bg-black/30 p-2 rounded text-xs text-zinc-300 h-20 border border-transparent focus:border-blue-500 outline-none resize-none"
                                        />
                                    </div>
                                    
                                    <div className="flex gap-2 mt-2">
                                        <AppButton onClick={() => setIsScheduleModalOpen(true)} size="sm" icon={Target} className="flex-[2] text-xs bg-blue-600 hover:bg-blue-500 border-none">
                                            Retarget Setup
                                        </AppButton>
                                        <AppButton onClick={handleGenerateRemix} size="sm" variant="secondary" icon={WandSparkles} className="flex-1 text-xs">
                                            Redo
                                        </AppButton>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SocialProofStudio;